import { useQueryClient } from '@tanstack/react-query';
import { Button, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import tw from 'twin.macro';

import { PATH } from '@/Route';
import Tescriptions from '@/components/Tescriptions';
import StatusBadge from '@/components/order/StatusBadge';
import { ProductItemBox } from '@/components/product/ProductItem';
import { BANK_TRANSFER_INFO, type PRODUCT_ORDER_STATUS } from '@/constants';
import { useCancelOrderProdct } from '@/hooks/mutations/partner';
import { useProductOrder } from '@/hooks/queries/partner';
import { getQueryKey } from '@/utils/queryKeys';

import CancelOrderModal from './components/CancelOrderModal';

const ProductOrderHistoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isError } = useProductOrder({
    id: Number(id),
  });
  const handleClick = () => {
    navigate(PATH.MARKETING_PRODUCT.ORDER_HISTORY);
  };

  if (isError) {
    navigate(PATH.MARKETING_PRODUCT.ORDER_HISTORY, { replace: true });
    return null;
  }

  if (!data) {
    return null;
  }

  return (
    <section css={tw`flex flex-col gap-y-[48px]`}>
      <Tescriptions column={2} title="상품 정보">
        <Tescriptions.Item label="신청일">
          {dayjs(data.orderedAt).format('YYYY-MM-DD HH:mm')}
        </Tescriptions.Item>
        <Tescriptions.Item label="신청 상태">
          <StatusBadge
            status={data.status as keyof typeof PRODUCT_ORDER_STATUS}
          />
        </Tescriptions.Item>
        <Tescriptions.Item label="처리일">
          {data.completedAt
            ? dayjs(data.completedAt).format('YYYY-MM-DD HH:mm')
            : '-'}
        </Tescriptions.Item>
      </Tescriptions>

      <Tescriptions column={1} title="신청 지점">
        <Tescriptions.Item label="지점명">
          {data.branch.branchName}
        </Tescriptions.Item>
      </Tescriptions>

      <ProductItemBox
        productId={data.id}
        title="신청 상품"
        url={data.bannerImageUrl}
        name={data.productName}
        summary={data.summary}
        price={data.price}
        discountPrice={data.discountPrice}
      />

      {data.additionalDescription && (
        <Tescriptions column={2} title="추가 입력 문항">
          <Tescriptions.Item label="관리자 질문">
            {data.additionalDescription || '-'}
          </Tescriptions.Item>
          <Tescriptions.Item label="파트너 답변">
            {data.additionalDescriptionRespond || '-'}
          </Tescriptions.Item>
        </Tescriptions>
      )}

      <Tescriptions column={2} title="결제 수단">
        <Tescriptions.Item label="결제 수단">
          {BANK_TRANSFER_INFO.METHOD_NAME}
        </Tescriptions.Item>
        <Tescriptions.Item label="계좌 정보">
          <div css={tw`flex flex-col `}>
            <Typography.Text css={tw`font-semibold`}>
              {BANK_TRANSFER_INFO.ACCOUNT}
            </Typography.Text>
            <Typography.Text css={tw`text-[rgba(0, 0, 0, 0.45)]`}>
              입금시 파트너 명의로 입금해주세요
            </Typography.Text>
          </div>
        </Tescriptions.Item>
      </Tescriptions>

      <Button onClick={handleClick} css={tw`m-auto`}>
        목록으로
      </Button>
    </section>
  );
};

export default ProductOrderHistoryDetail;

ProductOrderHistoryDetail.titleExtra = () => {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const query = useProductOrder({
    id: Number(id),
  });

  const [cancelModal, setCancelModal] = useState(false);

  const mutation = useCancelOrderProdct({
    onSuccess: () => {
      message.success('성공적으로 취소되었어요');
      queryClient.invalidateQueries({
        queryKey: getQueryKey('getProductOrder', {
          id: Number(id),
        }),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey('getProductOrders'),
      });
    },
  });

  if (!query.data) {
    return null;
  }

  return (
    query.data.status !== 'CANCELLED' && (
      <>
        <CancelOrderModal
          isOpen={cancelModal}
          onClose={() => setCancelModal(false)}
          orderId={Number(id)}
        />
        <Button
          color="danger"
          variant="solid"
          onClick={() => {
            setCancelModal(true);
          }}
        >
          신청 취소
        </Button>
      </>
    )
  );
};
